import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatMoney } from '../api/bookingApi';
import { useAuth } from '../auth/AuthContext';
import { useBooking } from '../booking/BookingContext';

function ticketRoute(t) {
  const trip = t?.trip;
  if (trip?.originName || trip?.destinationName) {
    return `${trip.originName || '—'} → ${trip.destinationName || '—'}`;
  }
  return t?.reference || 'Ticket';
}

export default function TicketsPage() {
  const auth = useAuth();
  const bookingCtx = useBooking();
  const navigate = useNavigate();
  const [removing, setRemoving] = useState('');

  const openTicket = (t) => {
    bookingCtx.setBooking({
      ...t,
      status: t.status || 'paid',
    });
    navigate('/book/ticket');
  };

  const remove = async (ref) => {
    if (!ref) return;
    setRemoving(ref);
    try {
      await auth.forgetTicket(ref);
    } finally {
      setRemoving('');
    }
  };

  const sorted = useMemo(() => auth.tickets || [], [auth.tickets]);

  return (
    <div className="min-h-[70vh] bg-gradient-to-b from-pink-50 to-white py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-superfeo-pink">
              My tickets
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {auth.isLoggedIn
                ? 'Saved on this device and your Superfeo account.'
                : 'Saved on this device. Create an optional account to keep them if you change browsers.'}
            </p>
          </div>
          <div className="flex gap-2">
            {auth.isLoggedIn ? (
              <Link
                to="/account"
                className="text-sm font-semibold text-superfeo-pink bg-pink-50 px-3 py-2 rounded-lg"
              >
                Account
              </Link>
            ) : (
              <Link
                to="/account"
                className="text-sm font-semibold bg-superfeo-pink text-white px-3 py-2 rounded-lg"
              >
                Log in / Register
              </Link>
            )}
          </div>
        </div>

        {!auth.ready ? (
          <p className="text-center text-gray-500 py-16">Loading tickets…</p>
        ) : sorted.length === 0 ? (
          <div className="bg-white rounded-2xl border border-pink-100 p-10 text-center">
            <p className="font-heading font-bold text-xl text-gray-800 mb-2">
              No tickets yet
            </p>
            <p className="text-sm text-gray-500 mb-6">
              When you complete a payment, your e-ticket appears here automatically.
            </p>
            <Link
              to="/book"
              className="inline-block bg-superfeo-pink text-white font-bold px-5 py-3 rounded-xl"
            >
              Book a trip
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {sorted.map((t) => (
              <li
                key={t.reference || t.tripId}
                className="bg-white rounded-2xl border border-pink-100 p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-gray-900">{ticketRoute(t)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Ref · {t.reference || '—'}
                      {t.trip?.date ? ` · ${t.trip.date}` : ''}
                      {t.trip?.departureTime ? ` · ${t.trip.departureTime}` : ''}
                    </p>
                    <p className="text-sm font-semibold text-superfeo-pink mt-2">
                      {formatMoney(t.amount, t.currency || 'TZS')}
                    </p>
                    {Array.isArray(t.seats) && t.seats.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Seat{t.seats.length > 1 ? 's' : ''} {t.seats.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 min-w-[8rem]">
                    <button
                      type="button"
                      onClick={() => openTicket(t)}
                      className="bg-superfeo-pink text-white text-sm font-bold py-2 px-3 rounded-xl"
                    >
                      Open ticket
                    </button>
                    <button
                      type="button"
                      disabled={removing === t.reference}
                      onClick={() => remove(t.reference)}
                      className="text-sm font-semibold text-gray-500 hover:text-red-600 py-1"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
